import { debounce, formatters, parsers } from "@fraction/shared";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { Dispatch, createContext, useCallback, useEffect, useMemo, useState } from "react";
import analytics, { trackEvent } from "src/analytics";
import { AnalyticsEvent } from "src/analytics/events";
import fraction, { ChecklistApp } from "src/api/fraction";
import { setScopeForApp } from "src/api/sentry";
import { LoadingApplication, SubmitConfirmation } from "src/apps/AppFlow/pages";
import { useTestApplicationQuery } from "src/apps/AppFlow/queries";
import { ModalBox } from "src/components";
import { IllustratedModalContent } from "src/components/InnerModals";
import {
  useCookieTracker,
  useCurrentRate,
  useEffectOnce,
  useModal,
  useQueryParams,
  useRedirect,
  useToken,
} from "src/hooks";
import useReducerPageState, { ExtractorPath, PageStateAction } from "src/hooks/useReducerPageState";
import { ProblemIllustration } from "src/images";
import { Logger } from "src/log";
import { selectApplicationLocation, selectFullApplication } from "src/selectors";
import { selectApplicationFlowState } from "src/selectors/appFlowState";
import { createStyles } from "src/styles";
import { Applicant, MortgageInformation } from "src/types/Applicant";
import { clean } from "src/utilities/object";
import { getTrackingContext } from "src/utilities/tracking";
import { v4 as uuid } from "uuid";

import ApplicationFlowLayout, { ApplicationFlowState } from "./Layout";

const LOGGER = new Logger("screens/ApplicationFlow");

const INITIAL_STATE: ApplicationFlowState = {
  applicants: [
    {
      id: uuid(),
      priority: 0,
    },
  ],
  isComplete: false,
};

export const ApplicationFlowStateContext = createContext<{
  state: ApplicationFlowState;
  dispatch?: Dispatch<PageStateAction<ApplicationFlowState>>;
}>({ state: INITIAL_STATE, dispatch: undefined });

const styles = createStyles({
  submitConfirmation: {
    zIndex: -1,
  },
});

export type ApplicationFlowStatePaths = ExtractorPath<ApplicationFlowState>;

const getApplicantTitle = (applicant: { name?: string }, idx: number, applicantsCount: number) => {
  if (applicant?.name) {
    return formatters.user.possessive(parsers.user.parseName(applicant.name).first);
  }
  if (applicantsCount > 1 && idx > 0) {
    return `Applicant ${idx + 1}`;
  }
  return "Your";
};

const INITIAL_NAV_SECTIONS = [
  {
    sectionHeader: "Property information",
    pageId: "propertyLocation",
  },
];

const fetchApp = debounce(fraction.getApplication, 500, { leading: true });

enum HydrationStatus {
  UN_HYDRATED = "un_hydrated",
  HYDRATING = "hydrating",
  HYDRATED = "hydrated",
}

const ApplicationFlow = () => {
  useCookieTracker();
  const [hydrationStatus, setHydrationStatus] = useState<HydrationStatus>(HydrationStatus.UN_HYDRATED);
  const ldClient = useLDClient();
  const { showModal, closeModal } = useModal();
  const { test } = useQueryParams();

  const { data: initialTestState, isLoading } = useTestApplicationQuery(test);

  const handleNext = useCallback(({ key, state }: { key: string; state: ApplicationFlowState }) => {
    let app: parsers.application.FullApplication;
    try {
      app = parsers.application.FULL_APPLICATION.parse(
        clean(selectFullApplication(clean(state), { defaults: false }))
      );

      // If applicant(s) email(s) are missing, return and don't track with Sentry
      parsers.application.HAS_APPLICANTS_WITH_EMAIL.parse(app);
    } catch {
      return;
    }

    setScopeForApp(app);
    if (key === "applicants") {
      if (app?.applicants?.[0]?.email) {
        analytics.identify(app?.applicants?.[0]?.email, app?.applicants?.[0], getTrackingContext(app));
      }
      trackEvent(AnalyticsEvent.PARTIAL_APP_SUBMITTED, app, getTrackingContext(app));
    }

    // Submissions now happen explicitly on each page
  }, []);

  const { state, extract, dispatch } = useReducerPageState<ApplicationFlowState>(
    "fullApplication",
    INITIAL_STATE,
    { onNext: handleNext }
  );

  // if we have an initial test value, set the app flow to that
  useEffectOnce(
    () => {
      if (!initialTestState) {
        // shouldn't happen due to the hook's precheck, just helps along the type system
        return;
      }
      dispatch({ type: "replace", value: selectApplicationFlowState(initialTestState) });
      setTimeout(() => {
        setHydrationStatus(HydrationStatus.HYDRATED);
      }, 300);
    },
    [initialTestState],
    initialTestState === undefined
  );

  const applicationFlowStateContextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  const params = useQueryParams();
  const { isSet: tokenSet } = useToken();

  useEffectOnce(
    () => {
      const fetch = async () => {
        setHydrationStatus(HydrationStatus.HYDRATING);
        let foundApplication: ChecklistApp | undefined;
        try {
          foundApplication = await fetchApp(params.eid);
          if (foundApplication?.brokers?.[0]?.id) {
            // set url param broker_id to the first broker id
            window.history.replaceState(
              {},
              "",
              `${window.location.pathname}?eid=${foundApplication.eid}&broker_id=${foundApplication.brokers[0].id}`
            );
          }
        } catch (err) {
          LOGGER.exception(err, `Unable to fetch app from id ${params.eid}`);
        } finally {
          setHydrationStatus(HydrationStatus.UN_HYDRATED);
        }

        if (foundApplication) {
          dispatch({ type: "replace", value: selectApplicationFlowState(foundApplication) });
          setHydrationStatus(HydrationStatus.HYDRATED);
        } else {
          showModal({
            message:
              "There was an error fetching your application. Please refresh to try again, or proceed from the beginning.",
            actions: [
              { text: "Proceed", type: "inverse", action: closeModal },
              { text: "Refresh", action: handleClickRefresh },
            ],
          });
        }
      };
      fetch();
    },
    [params, tokenSet],
    !params?.eid || !tokenSet
  );

  const handleCloseSubpage = useCallback((subpagePath: ApplicationFlowStatePaths) => {
    LOGGER.log("Closing subpage", subpagePath);
  }, []);

  const handleBackToHome = useRedirect(
    `https://www.fraction.com?submitted=true&email=${state.applicants?.[0]?.email?.email}`
  );

  const applicants = extract("applicants");
  const primaryPropertyMortgages = extract("primaryPropertyMortgages");

  const applicantNavSections = useMemo(
    () => [
      ...(applicants?.value || []).reduce((acc, applicant, idx, applicantsValue) => {
        const applicantTitle = getApplicantTitle(applicant, idx, applicantsValue.length);

        return [
          ...acc,
          {
            sectionHeader: `${applicantTitle} Personal Information`,
            pageId: `applicants,${idx},phoneAndContactMethodPreferences`,
          },
          {
            sectionHeader: `${applicantTitle} Financial Information`,
            pageId: `otherOwnedProperties-${applicant.id}`,
          },
        ];
      }, INITIAL_NAV_SECTIONS),
      {
        sectionHeader: "Review",
        pageId: "Review",
      },
    ],
    [applicants]
  );

  const { isWashington } = selectApplicationLocation(state.propertyLocation);

  const handleBackFromReview = useCallback(() => {
    analytics.track("goBack", { goingBackFrom: "Review" });
  }, []);

  const handleNextFromReview = useCallback((...args: any[]) => {
    trackEvent(AnalyticsEvent.SUBMITTED, { submittedValue: "Review" });
  }, []);

  const handleClickRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const finalPageProps = {
    onBack: handleBackFromReview,
    onNext: handleNextFromReview,
  };

  const propertyLocation = extract("propertyLocation");

  // prefetch all rates to make sure rate api is functioning properly, otherwise will abort the whole appflow
  const { rate: rate5, isFetched: isFetched5 } = useCurrentRate(propertyLocation.value?.administrativeArea, {
    logger: LOGGER,
  });

  // will show a blocking modal to stop the appflow if fail to get rates after 3 retries
  const fetchRateFailed = isFetched5 && !rate5;

  // When the main applicant receives a userId, assign it as the current user for launchdarkly
  const mainApplicantId = useMemo(
    () => (applicants.value?.length ? applicants.value[0].userId : null),
    [applicants.value]
  );
  const mainApplicantEmail = useMemo(
    () => (applicants.value?.length ? applicants.value[0].email?.email : null),
    [applicants.value]
  );
  useEffect(() => {
    if (ldClient && mainApplicantId && mainApplicantEmail) {
      ldClient.identify({ key: mainApplicantId, email: mainApplicantEmail });
    }
  }, [mainApplicantId, mainApplicantEmail, ldClient]);

  if (hydrationStatus === HydrationStatus.HYDRATING || isLoading) {
    return <LoadingApplication />;
  }

  return (
    <>
      <ApplicationFlowStateContext.Provider value={applicationFlowStateContextValue}>
        {state.isComplete ? (
          <SubmitConfirmation style={styles.submitConfirmation} onNext={handleBackToHome} />
        ) : (
          <ApplicationFlowLayout
            state={state}
            extractor={extract}
            dispatch={dispatch}
            navSections={applicantNavSections}
            continueFlow={hydrationStatus === HydrationStatus.HYDRATED}
            propertyAndLoanInformationSection={{
              propertyLocation: extract("propertyLocation"),
              propertyValue: extract("propertyValue"),
              amountRequested: extract("amountRequested"),
              primaryPropertyMortgages,
              applicants,
              propertyType: extract("propertyType"),
              useOfFunds: extract("useOfFunds"),
              creditScoreGuess: extract("creditScoreGuess"),
            }}
            primaryPropertyMortgageSections={(primaryPropertyMortgages?.value || []).map(
              (mortgage: MortgageInformation, index: number) => ({
                mortgageRateAndType: extract(["primaryPropertyMortgages", index, "mortgageRateAndType"]),
                mortgageBalanceAndLender: extract([
                  "primaryPropertyMortgages",
                  index,
                  "mortgageBalanceAndLender",
                ]),
                mortgagePayments: extract(["primaryPropertyMortgages", index, "mortgagePayments"]),
              })
            )}
            sharedItems={{
              assets: extract("assets"),
              debts: extract("debts"),
              otherOwnedProperties: extract("otherOwnedProperties"),
              incomeSources: extract("incomeSources"),
            }}
            applicantInformationSections={
              applicants?.value?.map((applicant: Applicant, index: number) => ({
                address: extract(["applicants", index, "address"]),
                name: extract(["applicants", index, "name"]),
                email: extract(["applicants", index, "email"]),
                phoneAndContactMethodPreferences: extract([
                  "applicants",
                  index,
                  "phoneAndContactMethodPreferences",
                ]),
                dependents: extract(["applicants", index, "dependents"]),
                employmentStatus: extract(["applicants", index, "employmentStatus"]),
                bankruptcyStatus: extract(["applicants", index, "bankruptcyStatus"]),
                timeSinceBankruptcy: extract(["applicants", index, "timeSinceBankruptcy"]),
                race: extract(["applicants", index, "race"]),
                ethnicity: extract(["applicants", index, "ethnicity"]),
                sex: extract(["applicants", index, "sex"]),
                militaryStatus: extract(["applicants", index, "militaryStatus"]),
                maritalStatus: extract(["applicants", index, "maritalStatus"]),
                socialXNumber: extract(["applicants", index, "socialXNumber"]),
                residencyStatus: extract(["applicants", index, "residencyStatus"]),
                dateOfBirth: extract(["applicants", index, "dateOfBirth"]),
                waCivilUnionAddendum: extract(["applicants", index, "waCivilUnionAddendum"]),
                applicant,
              })) || []
            }
            idVerificationQuestions={{
              idVerificationQuestions: extract("idVerificationQuestions"),
            }}
            reviewSection={{
              applicationReview: isWashington ? extract("applicationReview") : finalPageProps,
              importantTermsAndInfo: finalPageProps,
            }}
            onCloseSubpage={handleCloseSubpage}
          />
        )}
        <ModalBox open={fetchRateFailed} closeButton={false}>
          <IllustratedModalContent
            actions={[]}
            header="Please try again later"
            text="We are unable to fetch current rates at this time. Apologies for the inconvenience."
            image={<ProblemIllustration height={167} width={188} />}
          />
        </ModalBox>
      </ApplicationFlowStateContext.Provider>
    </>
  );
};

export default ApplicationFlow;
